import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@material-ui/core';
import styled from 'styled-components';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { resolveSagisClaim } from 'helpers/utils';
import ImportImagineDocumentButton from 'containers/CourtManagerLegalProceedings/ImagineDocuments/ImportButton';
import { translate } from 'containers/services/translate';
import messages from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/messages';
import {
  ExpertReportType,
  IVictimExpertise,
  TvictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import {
  EMPTY_DOCUMENT,
  LISTINGS,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import { get, isEmpty, isNil } from 'lodash';
import { formatPourcentage } from 'containers/services/formatNumberInput';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { getDocumentId } from 'containers/common/LegalProceedings/ExpertReport/utils';
import { DOCUMENT_TYPES } from 'constants/global';
import { Config, Listings } from 'containers/AppContainer/typings';
import {
  formatParamValue,
  getEvaluationConfigFields,
  showUpdateButton,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/utils';
import ExpertReportDecision from 'containers/CourtManagerLegalProceedings/ExpertReport/Decision';
import { arrayTranslate } from 'containers/services/translate';
import { displayErrorText } from 'containers/services/validateForm';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { medicalExpertisesValidator } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/form-validations';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { TYPE } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/constants';

const Container = styled.div`
  .errorSelect {
    color: red;
  }
`;
export interface MedicalExpertiseProps {
  intl: IntlShape;
  readOnly?: boolean;
  victimExpertReport: IVictimExpertise;
  setVictimExpertReport: Function;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  expertiseJudgementAddDocumentId?: string;
  setDocumentId: Function;
  setDocument: Function;
  documentIndex: number;
  isManager?: boolean;
  listings: Listings;
  isDecisionDialgOpen?: boolean;
  setIsDecisionDialgOpen?: Function;
  validateVictimExpertise?: any;
  isRejected?: boolean;
  documentId?: string;
  isAppeal?: boolean;
  isRecap?: boolean;
  isAssignedLawyer: boolean;
  isExpertReportValidated: boolean;
  username: string;
  isLawyerAssignedJudiciary?: boolean;
  expertReportState: ExpertReportType;
  imagineDocuments: any;
  claimId: number;
  policyInfo: any;
  requestGetDocByDocumentumId: Function;
}

export const MedicalExpertise: React.FC<MedicalExpertiseProps> = ({
  victimExpertReport,
  validateVictimExpertise,
  isAppeal,
  ...props
}): JSX.Element => {
  const [isInputChanged, setIsInputChanged] = useState<boolean>(true);
  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const validateMedical = medicalExpertisesValidator(
    get(victimExpertReport, 'victimExpertise', {}) as TvictimExpertise,
  );
  const { message } =
    displayErrorText(
      validateMedical,
      translateArrayErrorMessages(validateMedical, true),
    ) || {};

  const handleChange = (
    e: React.ChangeEvent<{ value: unknown; name?: string }>,
  ) => {
    setIsInputChanged(false);
    const vicExpertReport = { ...victimExpertReport };
    const { name, value } = e.target;

    vicExpertReport.victimExpertise[name as string] = value;

    if (name === VICTIM_EXPERTISE_FIELDS.IPP) {
      vicExpertReport.victimExpertise[name] = formatPourcentage(
        value as string,
      );
    } else if (name === VICTIM_EXPERTISE_FIELDS.ITT) {
      vicExpertReport.victimExpertise[
        name as string
      ] = (value as string).replace(/[^0-9]/g, '');
    } else if (name === VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE) {
      vicExpertReport.victimExpertise[name as string] =
        value === '' ? null : value;
    }

    vicExpertReport.victimExpertise['injuryPostKey'] =
      victimExpertReport['injuryPostKey'];

    props.setVictimExpertReport(vicExpertReport);
  };

  const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vicExpertReport = { ...victimExpertReport };
    const { name, checked } = e.target;
    vicExpertReport.victimExpertise[name as string] = checked;

    vicExpertReport.victimExpertise['injuryPostKey'] =
      victimExpertReport['injuryPostKey'];

    props.setVictimExpertReport(vicExpertReport);
  };

  const [expertReportDocument, setExpertReportDocument] = useState({
    ...EMPTY_DOCUMENT,
  });

  const [oldExpertReportDocument, setOldExpertReportDocument] = useState({
    ...EMPTY_DOCUMENT,
  });

  const setExpertReportDoc = (document: CorporealDocument) => {
    const vicExpertReport = { ...victimExpertReport };
    if (
      getDocumentId(
        String(victimExpertReport.id) as string,
        props.expertiseJudgementAddDocumentId as string,
      ) === props.documentId
    ) {
      vicExpertReport.victimExpertise[
        VICTIM_EXPERTISE_FIELDS.VICTIM_EXPERTISE_DOCUMENTS
      ] = [{ ...document, id: get(oldExpertReportDocument, 'id', null) }];

      setExpertReportDocument(document as any);
      props.setVictimExpertReport(vicExpertReport);
      props.resetUploadedDocument();
    }
  };

  useEffect(() => {
    setOldExpertReportDocument(
      get(
        victimExpertReport.victimExpertise,
        [VICTIM_EXPERTISE_FIELDS.VICTIM_EXPERTISE_DOCUMENTS, '0'],
        EMPTY_DOCUMENT,
      ),
    );
  }, [victimExpertReport]);

  const IsChecked = get(
    victimExpertReport.victimExpertise,
    VICTIM_EXPERTISE_FIELDS.IS_DEATH_RELATED_TO_CLAIM,
    false,
  );

  const {
    careerPrejudice,
    schoolingPrejudice,
    aestheticDamageWithConsequence,
    aestheticDamageWithoutConsequence,
  } = getEvaluationConfigFields(victimExpertReport.victimExpertise);

  return (
    <div className="global-expertise-container">
      <div className="medical-wrapper">
        <div className="medical-form">
          <Container>
            {victimExpertReport['injuryPostKey'] !== '2' && (
              <>
                <TextField
                  label={translate(messages.medicalPharmaceuticalFees, props)}
                  name={VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES}
                  value={String(
                    get(
                      victimExpertReport.victimExpertise,
                      VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES,
                      '',
                    ),
                  )}
                  type="number"
                  onChange={handleChange}
                  InputProps={{ disabled: props.readOnly }}
                  className="medical-item-field"
                  error={
                    get(message, [
                      VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES,
                    ]) &&
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged)
                  }
                  helperText={
                    message[
                      VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES
                    ] &&
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged)
                  }
                />

                <TextField
                  label={translate(messages.itt, props)}
                  name={VICTIM_EXPERTISE_FIELDS.ITT}
                  value={String(
                    parseInt(
                      get(
                        victimExpertReport.victimExpertise,
                        VICTIM_EXPERTISE_FIELDS.ITT,
                        '',
                      ),
                    ),
                  )}
                  type="number"
                  onChange={handleChange}
                  InputProps={{ disabled: props.readOnly }}
                  className="medical-item-field"
                  error={
                    get(message, [VICTIM_EXPERTISE_FIELDS.ITT]) &&
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged)
                  }
                  helperText={
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged) &&
                    message[VICTIM_EXPERTISE_FIELDS.ITT]
                  }
                />
                <TextField
                  label={translate(messages.ipp, props)}
                  name={VICTIM_EXPERTISE_FIELDS.IPP}
                  value={String(
                    get(
                      victimExpertReport.victimExpertise,
                      VICTIM_EXPERTISE_FIELDS.IPP,
                      '',
                    ),
                  )}
                  type="number"
                  onChange={handleChange}
                  InputProps={{
                    disabled: props.readOnly,
                    endAdornment: (
                      <InputAdornment position="end">
                        {translate(messages.expertReportpourcentage, props)}
                      </InputAdornment>
                    ),
                  }}
                  className="medical-item-field"
                  error={
                    get(message, [VICTIM_EXPERTISE_FIELDS.IPP]) &&
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged)
                  }
                  helperText={
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged) &&
                    message[VICTIM_EXPERTISE_FIELDS.IPP]
                  }
                />
                <FormControl
                  className="medical-item-field"
                  disabled={props.readOnly}
                >
                  <InputLabel
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {translate(messages.thirdPartyAppeal, props)}
                  </InputLabel>
                  <Select
                    name={VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL}
                    value={String(
                      get(
                        victimExpertReport.victimExpertise,
                        VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL,
                        '',
                      ),
                    )}
                    onChange={handleChange}
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {get(props.listings, LISTINGS.THIRD_PARTY_APPEALS, []).map(
                      (item: Config) => (
                        <MenuItem value={item.percentage} key={item.percentage}>
                          {formatParamValue(item)}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  {!!get(message, [
                    VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL,
                  ]) &&
                    !get(
                      victimExpertReport,
                      'victimExpertise.isNew',
                      false,
                    ) && (
                      <FormHelperText className="errorSelect">
                        {message[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL]}
                      </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                  className="medical-item-field"
                  disabled={props.readOnly}
                >
                  <InputLabel
                    error={
                      get(message, [VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {translate(messages.pretiumDoloris, props)}
                  </InputLabel>
                  <Select
                    name={VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS}
                    value={String(
                      get(
                        victimExpertReport.victimExpertise,
                        VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS,
                        '',
                      ),
                    )}
                    onChange={handleChange}
                    disabled={props.readOnly}
                    error={
                      get(message, [VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {get(props.listings, LISTINGS.PRETIUM_DOLORIS, []).map(
                      (item: Config) => (
                        <MenuItem value={item.percentage} key={item.percentage}>
                          {formatParamValue(item)}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  {get(message, [VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]) &&
                    !get(
                      victimExpertReport,
                      'victimExpertise.isNew',
                      false,
                    ) && (
                      <FormHelperText className="errorSelect">
                        {message[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]}
                      </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                  className="medical-item-field"
                  disabled={props.readOnly}
                >
                  <InputLabel
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {translate(
                      messages.aestheticDamageWithoutConsequence,
                      props,
                    )}
                  </InputLabel>
                  <Select
                    name={
                      VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE
                    }
                    value={aestheticDamageWithoutConsequence}
                    onChange={handleChange}
                    disabled={props.readOnly}
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {get(
                      props.listings,
                      LISTINGS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE,
                      [],
                    ).map((item: Config) => (
                      <MenuItem value={item.percentage} key={item.percentage}>
                        {formatParamValue(item)}
                      </MenuItem>
                    ))}
                  </Select>
                  {get(message, [VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]) &&
                    !get(
                      victimExpertReport,
                      'victimExpertise.isNew',
                      false,
                    ) && (
                      <FormHelperText className="errorSelect">
                        {
                          message[
                            VICTIM_EXPERTISE_FIELDS
                              .AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE
                          ]
                        }
                      </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                  className="medical-item-field"
                  disabled={props.readOnly}
                >
                  <InputLabel
                    error={
                      !!get(message, [
                        VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {translate(messages.aestheticDamageWithConsequence, props)}
                  </InputLabel>
                  <Select
                    name={
                      VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE
                    }
                    value={aestheticDamageWithConsequence}
                    onChange={handleChange}
                    disabled={props.readOnly}
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {get(
                      props.listings,
                      LISTINGS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE,
                      [],
                    ).map((item: Config) => (
                      <MenuItem value={item.percentage} key={item.percentage}>
                        {formatParamValue(item)}
                      </MenuItem>
                    ))}
                  </Select>
                  {get(message, [VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS]) &&
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged) &&
                    !aestheticDamageWithoutConsequence && (
                      <FormHelperText className="errorSelect">
                        {
                          message[
                            VICTIM_EXPERTISE_FIELDS
                              .AESTHETIC_DAMAGE_WITH_CONSEQUENCE
                          ]
                        }
                      </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                  className="medical-item-field"
                  disabled={props.readOnly}
                >
                  <InputLabel
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {translate(messages.careerPrejudice, props)}
                  </InputLabel>
                  <Select
                    name={VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE}
                    value={careerPrejudice}
                    onChange={handleChange}
                    disabled={props.readOnly}
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {!isEmpty(
                      get(props.listings, LISTINGS.CAREER_PREJUDICE, []),
                    ) &&
                      get(props.listings, LISTINGS.CAREER_PREJUDICE, []).map(
                        (item: Config) => (
                          <MenuItem
                            value={item.percentage}
                            key={item.percentage}
                          >
                            {formatParamValue(item)}
                          </MenuItem>
                        ),
                      )}
                  </Select>
                  {get(message, [VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE]) &&
                    !get(
                      victimExpertReport,
                      'victimExpertise.isNew',
                      false,
                    ) && (
                      <FormHelperText className="errorSelect">
                        {message[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE]}
                      </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                  className="medical-item-field"
                  disabled={props.readOnly}
                >
                  <InputLabel
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE,
                      ]) &&
                      (!get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) ||
                        !isInputChanged)
                    }
                  >
                    {translate(messages.schoolingPrejudice, props)}
                  </InputLabel>
                  <Select
                    name={VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE}
                    value={schoolingPrejudice}
                    onChange={handleChange}
                    disabled={props.readOnly}
                    displayEmpty
                    error={
                      get(message, [
                        VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE,
                      ]) &&
                      !get(
                        victimExpertReport,
                        'victimExpertise.isNew',
                        false,
                      ) &&
                      !careerPrejudice
                    }
                  >
                    {get(props.listings, LISTINGS.SCHOOLING_PREJUDICE, []).map(
                      (item: Config) => (
                        <MenuItem value={item.percentage} key={item.percentage}>
                          {formatParamValue(item)}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  {get(message, [
                    VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE,
                  ]) &&
                    (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                      !isInputChanged) &&
                    !careerPrejudice && (
                      <FormHelperText className="errorSelect">
                        {message[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE]}
                      </FormHelperText>
                    )}
                </FormControl>
              </>
            )}

            {victimExpertReport['injuryPostKey'] === '2' && (
              <TextField
                label={translate(messages.medicalvictimRevenu, props)}
                name={VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE}
                value={String(
                  get(
                    victimExpertReport.victimExpertise,
                    VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE,
                    '',
                  ),
                )}
                type="number"
                onChange={handleChange}
                InputProps={{ disabled: props.readOnly }}
                className="medical-item-field"
                error={
                  get(message, [VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE]) &&
                  (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                    !isInputChanged)
                }
                helperText={
                  (!get(victimExpertReport, 'victimExpertise.isNew', false) ||
                    !isInputChanged) &&
                  message[VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE]
                }
              />
            )}
            {victimExpertReport['injuryPostKey'] === '2' && (
              <>
                <TextField
                  label={translate(messages.causalityLink, props)}
                  name={VICTIM_EXPERTISE_FIELDS.CAUSALITY_LINK}
                  value={
                    String(
                      get(
                        victimExpertReport.victimExpertise,
                        VICTIM_EXPERTISE_FIELDS.CAUSALITY_LINK,
                        '',
                      ),
                    ) === 'null'
                      ? get(
                          victimExpertReport.victimExpertise,
                          VICTIM_EXPERTISE_FIELDS.CAUSALITY_LINK,
                          '',
                        )
                      : String(
                          get(
                            victimExpertReport.victimExpertise,
                            VICTIM_EXPERTISE_FIELDS.CAUSALITY_LINK,
                            '',
                          ),
                        )
                  }
                  onChange={handleChange}
                  InputProps={{ disabled: props.readOnly }}
                  className="medical-item-field"
                />
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={VICTIM_EXPERTISE_FIELDS.IS_DEATH_RELATED_TO_CLAIM}
                        checked={IsChecked}
                        onChange={handleCheckBoxChange}
                        color="primary"
                        disabled={props.readOnly}
                      />
                    }
                    label={translate(messages.isDeathRelatedToClaim, props)}
                  />
                </div>
              </>
            )}
            <Grid container direction="row" spacing={2}>
              <Grid item xs>
                <UploadDocumentBloc
                  document={oldExpertReportDocument}
                  setDocument={props.setDocument}
                  uploadDocument={props.uploadDocument}
                  uploadedDocument={props.uploadedDocument}
                  resetUploadedDocument={props.resetUploadedDocument}
                  docType={
                    !isAppeal
                      ? DOCUMENT_TYPES.EXPERT_REPORT
                      : DOCUMENT_TYPES.EXPERT_REPORT_APPEAL
                  }
                  index={props.documentIndex}
                  docId={getDocumentId(
                    String(victimExpertReport.id) as string,
                    props.expertiseJudgementAddDocumentId as string,
                  )}
                  setDocumentId={props.setDocumentId}
                  shouldDisplay={false}
                />
              </Grid>
              <Grid item style={{ marginTop: '20px' }}>
                {!isEmpty(props.imagineDocuments) &&
                  props.isManager &&
                  isNil(get(victimExpertReport.victimExpertise, 'id', null)) &&
                  featureService.hasFeature(
                    FEATURES.TRAITEMENT_IMAGINE_DOC,
                  ) && (
                    <ImportImagineDocumentButton
                      listings={props.listings}
                      managerNotifcationsInboxes={props.imagineDocuments}
                      claimId={props.claimId}
                      imagineDocuments={props.imagineDocuments}
                      claimSagisInfo={resolveSagisClaim(props.policyInfo)}
                      uploadDocument={props.uploadDocument}
                      uploadedDocument={props.uploadedDocument}
                      setUploadedDocs={props.setDocument}
                      documentName={
                        !isAppeal
                          ? DOCUMENT_TYPES.EXPERT_REPORT
                          : DOCUMENT_TYPES.EXPERT_REPORT_APPEAL
                      }
                      documentType={
                        !isAppeal
                          ? DOCUMENT_TYPES.EXPERT_REPORT
                          : DOCUMENT_TYPES.EXPERT_REPORT_APPEAL
                      }
                      documentId={getDocumentId(
                        String(victimExpertReport.id) as string,
                        props.expertiseJudgementAddDocumentId as string,
                      )}
                      document={oldExpertReportDocument}
                      setDocumentId={props.setDocumentId}
                      requestGetDocByDocumentumId={
                        props.requestGetDocByDocumentumId
                      }
                    />
                  )}
              </Grid>
            </Grid>
            {((props.isRejected &&
              (!isEmpty(get(victimExpertReport.victimExpertise, 'creator', ''))
                ? get(victimExpertReport.victimExpertise, 'creator', '') ===
                  props.username
                : props.isAssignedLawyer)) ||
              showUpdateButton(
                props.expertReportState,
                TYPE.VICTIM,
                get(victimExpertReport, 'victimExpertise', {}),
                props.isManager,
              )) && (
              <>
                <div className="add-recap-bloc-title">
                  {translate(messages.newDocument, props)}
                </div>
                <UploadDocumentBloc
                  document={expertReportDocument}
                  setDocument={setExpertReportDoc}
                  uploadDocument={props.uploadDocument}
                  uploadedDocument={props.uploadedDocument}
                  resetUploadedDocument={props.resetUploadedDocument}
                  docType={
                    !isAppeal
                      ? DOCUMENT_TYPES.EXPERT_REPORT
                      : DOCUMENT_TYPES.EXPERT_REPORT_APPEAL
                  }
                  index={props.documentIndex}
                  docId={getDocumentId(
                    String(victimExpertReport.id) as string,
                    props.expertiseJudgementAddDocumentId as string,
                  )}
                  setDocumentId={props.setDocumentId}
                  shouldDisplay={false}
                />
              </>
            )}
          </Container>
        </div>
        <Grid container direction="row">
          {get(victimExpertReport, 'victimExpertise.isOld') && (
            <Grid item xs={6} className="caduc-position">
              <article className="caduc-item">
                {translate(messages.deprecated, props)}
              </article>
            </Grid>
          )}
          {(featureService.hasFeature(FEATURES.EVOLUTION_P1)
            ? true
            : !get(victimExpertReport, 'victimExpertise.isOld')) &&
            (props.isLawyerAssignedJudiciary ||
              props.isManager ||
              userPrivileges.userHasPrivilege(
                PRIVILEGES.ISCOURTMONITORINGMANAGER,
              )) && (
              <Grid item>
                <section className="expert-report-decision-wrapper">
                  {get(victimExpertReport, 'victimExpertise.creator', '') && (
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '12px',
                        color: '#0072c6',
                      }}
                    >
                      {translate(messages.creatorTitle, props, {
                        lawyer: get(
                          victimExpertReport,
                          'victimExpertise.creator',
                          '',
                        ),
                      })}
                    </span>
                  )}
                </section>
                <ExpertReportDecision
                  expertise={victimExpertReport as any}
                  setExpertise={props.setVictimExpertReport}
                  isDecisionDialgOpen={props.isDecisionDialgOpen as boolean}
                  isManager={props.isManager as boolean}
                  uploadDocument={props.uploadDocument}
                  uploadedDocument={props.uploadedDocument}
                  resetUploadedDocument={props.resetUploadedDocument}
                  setIsDecisionDialgOpen={
                    props.setIsDecisionDialgOpen as Function
                  }
                  isExpertReportValidated={props.isExpertReportValidated}
                  expertReportState={props.expertReportState}
                  requestGetDocByDocumentumId={
                    props.requestGetDocByDocumentumId
                  }
                />
              </Grid>
            )}
        </Grid>
      </div>
    </div>
  );
};

MedicalExpertise.defaultProps = {
  readOnly: false,
};

export default injectIntl(MedicalExpertise);
